import { useState } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { APPLIES_TO_OPTIONS, GATE_KIND_META } from '../constants';
import type { Checkpoint, GateKind, PatientApplicability } from '../types';

/**
 * Booking gates organized by the three points in the booking funnel rather
 * than a flat toggle list. The section a gate lives in *is* its checkpoint, so
 * there's no separate checkpoint control — a gate is configured by where you
 * place it.
 *
 * Presentational only — BookingGatesFunnelSection supplies the data and
 * handlers — so it stays freely storyable.
 */

interface SectionDef {
  checkpoint: Checkpoint;
  step: number;
  title: string;
  blurb: string;
}

/** The booking funnel, top to bottom. */
const SECTIONS: SectionDef[] = [
  {
    checkpoint: 'before_calendar',
    step: 1,
    title: 'Before they see open times',
    blurb: 'Checks that must pass before the calendar is shown.',
  },
  {
    checkpoint: 'at_booking',
    step: 2,
    title: 'Required to book',
    blurb: 'Must be satisfied before a time is reserved.',
  },
  {
    checkpoint: 'after_booking',
    step: 3,
    title: 'Held until confirmed',
    blurb: 'They can book, but it stays pending until these are done.',
  },
];

/** Short titles used in the per-gate "Move to …" menu. */
const CHECKPOINT_TITLE: Record<Checkpoint, string> = {
  before_calendar: 'Before they see times',
  at_booking: 'Required to book',
  after_booking: 'Held until confirmed',
};

/** Compact labels for the per-gate applicability segmented control. */
const APPLIES_TO_SHORT: Record<PatientApplicability, string> = {
  new: 'New',
  returning: 'Returning',
  all: 'Everyone',
};

/**
 * Which sections each gate kind may occupy. A gate kind lives in exactly one
 * section at a time.
 *
 * Two kinds are intentionally non-placeable in MVP (empty arrays):
 * - `fit` is a fast-follow per the PRD — surfaced as a "Coming soon" teaser
 *   (see COMING_SOON) rather than a working gate.
 * - `eligibility` ships as enrichment (verify insurance + show coverage), not a
 *   blocking gate — see the During booking area below. (A future hard-gate mode
 *   would add `before_calendar` here.)
 */
const VALID_CHECKPOINTS_BY_GATE: Record<GateKind, Checkpoint[]> = {
  fit: [],
  card: ['at_booking', 'after_booking'],
  paperwork: ['at_booking', 'after_booking'],
  eligibility: [],
};

const ALL_GATE_KINDS = Object.keys(VALID_CHECKPOINTS_BY_GATE) as GateKind[];

interface ComingSoonCheck {
  checkpoint: Checkpoint;
  label: string;
  description: string;
}

/**
 * Planned-but-not-yet-functional checks, shown disabled in their section so
 * practices can see what's coming. `fit` is a fast-follow in the PRD: a short
 * pre-calendar questionnaire that filters by practice-defined rules.
 */
const COMING_SOON: ComingSoonCheck[] = [
  {
    checkpoint: 'before_calendar',
    label: 'Good-fit screening',
    description:
      'A 2–3 question screen so only patients who match your practice — by severity, age, or service — reach your calendar.',
  },
];

export interface FunnelGate {
  kind: GateKind;
  checkpoint: Checkpoint;
  appliesTo: PatientApplicability;
}

export interface BookingGatesFunnelViewProps {
  gates: FunnelGate[];
  showCoverage: boolean;
  subjectPlural: string;
  isLoading?: boolean;
  hasError?: boolean;
  onAddGate?: (kind: GateKind, checkpoint: Checkpoint) => void;
  onMoveGate?: (kind: GateKind, checkpoint: Checkpoint) => void;
  onRemoveGate?: (kind: GateKind) => void;
  onChangeAppliesTo?: (kind: GateKind, value: PatientApplicability) => void;
  onToggleCoverage?: (enabled: boolean) => void;
}

export function BookingGatesFunnelView({
  gates,
  showCoverage,
  subjectPlural,
  isLoading = false,
  hasError = false,
  onAddGate,
  onMoveGate,
  onRemoveGate,
  onChangeAppliesTo,
  onToggleCoverage,
}: BookingGatesFunnelViewProps) {
  const placedKinds = new Set(gates.map(gate => gate.kind));

  return (
    <Card
      elevation={1}
      sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}
    >
      <CardHeader
        avatar={<ShieldOutlinedIcon color="primary" />}
        title="Scheduling gates"
        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
        subheader={`Control which ${subjectPlural} can land on your calendar — and when each check happens.`}
      />
      <CardContent sx={{ pt: 0 }}>
        {hasError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Couldn’t load booking gates. Try refreshing.
          </Alert>
        )}
        {isLoading ? (
          <Stack sx={{ alignItems: 'center', py: 4 }}>
            <CircularProgress aria-label="Loading booking gates" />
          </Stack>
        ) : (
          <Stack spacing={2}>
            {SECTIONS.map(section => (
              <GateSection
                key={section.checkpoint}
                section={section}
                gates={gates}
                placedKinds={placedKinds}
                onAdd={onAddGate}
                onMove={onMoveGate}
                onRemove={onRemoveGate}
                onChangeAppliesTo={onChangeAppliesTo}
              />
            ))}

            <Divider sx={{ pt: 1 }}>
              <Typography variant="overline" color="text.secondary">
                During booking
              </Typography>
            </Divider>

            <Box
              sx={{ border: 1, borderColor: 'grey.200', borderRadius: 2, p: 2 }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Verify insurance at booking
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Patients add their plan and a photo of their card, and we run
                    a live coverage check — so you get verified benefits on file
                    and they see their estimated copay before picking a time.
                    Never turns anyone away.
                  </Typography>
                </Box>
                <Switch
                  checked={showCoverage}
                  onChange={(_event, checked) => onToggleCoverage?.(checked)}
                  slotProps={{
                    input: {
                      'aria-label': 'Verify insurance at booking',
                    },
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

interface GateSectionProps {
  section: SectionDef;
  gates: FunnelGate[];
  placedKinds: Set<GateKind>;
  onAdd?: (kind: GateKind, checkpoint: Checkpoint) => void;
  onMove?: (kind: GateKind, checkpoint: Checkpoint) => void;
  onRemove?: (kind: GateKind) => void;
  onChangeAppliesTo?: (kind: GateKind, value: PatientApplicability) => void;
}

function GateSection({
  section,
  gates,
  placedKinds,
  onAdd,
  onMove,
  onRemove,
  onChangeAppliesTo,
}: GateSectionProps) {
  const sectionGates = gates.filter(
    gate => gate.checkpoint === section.checkpoint,
  );
  const comingSoon = COMING_SOON.filter(
    check => check.checkpoint === section.checkpoint,
  );
  const addable = ALL_GATE_KINDS.filter(
    kind =>
      VALID_CHECKPOINTS_BY_GATE[kind].includes(section.checkpoint) &&
      !placedKinds.has(kind),
  );

  return (
    <Box sx={{ border: 1, borderColor: 'grey.200', borderRadius: 2, p: 2 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'baseline' }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 22,
            height: 22,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            alignSelf: 'center',
          }}
        >
          {section.step}
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {section.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {section.blurb}
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={1} sx={{ mt: 1.5 }}>
        {sectionGates.length === 0 && comingSoon.length === 0 && (
          <Typography
            variant="body2"
            color="text.disabled"
            sx={{ fontStyle: 'italic' }}
          >
            No checks here yet.
          </Typography>
        )}
        {sectionGates.map(gate => (
          <PlacedGateRow
            key={gate.kind}
            gate={gate}
            onMove={onMove}
            onRemove={onRemove}
            onChangeAppliesTo={onChangeAppliesTo}
          />
        ))}
        {comingSoon.map(check => (
          <ComingSoonRow
            key={check.label}
            label={check.label}
            description={check.description}
          />
        ))}
        <AddCheckPicker
          addable={addable}
          onAdd={kind => onAdd?.(kind, section.checkpoint)}
        />
      </Stack>
    </Box>
  );
}

interface PlacedGateRowProps {
  gate: FunnelGate;
  onMove?: (kind: GateKind, checkpoint: Checkpoint) => void;
  onRemove?: (kind: GateKind) => void;
  onChangeAppliesTo?: (kind: GateKind, value: PatientApplicability) => void;
}

function PlacedGateRow({
  gate,
  onMove,
  onRemove,
  onChangeAppliesTo,
}: PlacedGateRowProps) {
  const meta = GATE_KIND_META[gate.kind];
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const moveTargets = VALID_CHECKPOINTS_BY_GATE[gate.kind].filter(
    checkpoint => checkpoint !== gate.checkpoint,
  );

  return (
    <Stack
      direction="row"
      spacing={1.5}
      useFlexGap
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        border: 1,
        borderColor: 'grey.100',
        borderRadius: 1.5,
        bgcolor: 'grey.50',
        p: 1,
        pl: 1.5,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {meta.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {meta.description}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Applies to
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={gate.appliesTo}
          onChange={(_event, value: PatientApplicability | null) => {
            if (value) onChangeAppliesTo?.(gate.kind, value);
          }}
          aria-label={`${meta.label} applies to`}
          sx={{ bgcolor: 'background.paper' }}
        >
          {APPLIES_TO_OPTIONS.map(value => (
            <ToggleButton
              key={value}
              value={value}
              sx={{ textTransform: 'none', px: 1.25, py: 0.5 }}
            >
              {APPLIES_TO_SHORT[value]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <IconButton
          size="small"
          aria-label={`${meta.label} options`}
          onClick={event => setAnchor(event.currentTarget)}
        >
          <MoreVertOutlinedIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
        >
          {moveTargets.map(checkpoint => (
            <MenuItem
              key={checkpoint}
              onClick={() => {
                onMove?.(gate.kind, checkpoint);
                setAnchor(null);
              }}
            >
              Move to “{CHECKPOINT_TITLE[checkpoint]}”
            </MenuItem>
          ))}
          {moveTargets.length > 0 && <Divider />}
          <MenuItem
            onClick={() => {
              onRemove?.(gate.kind);
              setAnchor(null);
            }}
          >
            Remove
          </MenuItem>
        </Menu>
      </Stack>
    </Stack>
  );
}

interface ComingSoonRowProps {
  label: string;
  description: string;
}

/** A planned check, shown disabled with a "Coming soon" badge and no controls. */
function ComingSoonRow({ label, description }: ComingSoonRowProps) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      useFlexGap
      sx={{
        alignItems: 'center',
        flexWrap: 'wrap',
        border: '1px dashed',
        borderColor: 'grey.300',
        borderRadius: 1.5,
        p: 1,
        pl: 1.5,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.secondary' }}
          >
            {label}
          </Typography>
          <Chip
            label="Coming soon"
            size="small"
            variant="outlined"
            sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: 11 } }}
          />
        </Stack>
        <Typography variant="caption" color="text.disabled">
          {description}
        </Typography>
      </Box>
    </Stack>
  );
}

interface AddCheckPickerProps {
  addable: GateKind[];
  onAdd: (kind: GateKind) => void;
}

/**
 * Expands the "Add a check" affordance into a single-select group of
 * button-style options (radio semantics via an exclusive ToggleButtonGroup),
 * each carrying the check's name and a description so the choice is informed.
 */
function AddCheckPicker({ addable, onAdd }: AddCheckPickerProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<GateKind | null>(null);
  if (addable.length === 0) return null;

  const reset = () => {
    setOpen(false);
    setSelected(null);
  };
  const commit = () => {
    if (selected) {
      onAdd(selected);
      reset();
    }
  };

  if (!open) {
    return (
      <Button
        size="small"
        startIcon={<AddOutlinedIcon />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
      >
        Add a check
      </Button>
    );
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'primary.light',
        borderRadius: 1.5,
        bgcolor: 'action.hover',
        p: 1.5,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1 }}
      >
        Choose a check to add
      </Typography>
      <ToggleButtonGroup
        exclusive
        orientation="vertical"
        fullWidth
        value={selected}
        onChange={(_event, value: GateKind | null) => setSelected(value)}
      >
        {addable.map(kind => (
          <ToggleButton
            key={kind}
            value={kind}
            sx={{
              textTransform: 'none',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              textAlign: 'left',
              px: 1.5,
              py: 1.25,
              bgcolor: 'background.paper',
            }}
          >
            <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {GATE_KIND_META[kind].label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {GATE_KIND_META[kind].description}
              </Typography>
            </Stack>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1.5, justifyContent: 'flex-end' }}
      >
        <Button size="small" onClick={reset} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={commit}
          disabled={!selected}
          sx={{ textTransform: 'none' }}
        >
          Add
        </Button>
      </Stack>
    </Box>
  );
}
